import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useVirtualizer } from "@tanstack/react-virtual"
import { X } from "lucide-react"
import { useRef } from "react"
import {
  INQUIRY_STATUS_DISPLAY,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"
import { McPill } from "../../shared/McPill"

interface InquiryBulkSelectionListProps {
  /** The rows the user checked, captured at click time by useRowMultiSelect.
   * Nothing is re-fetched here: a draft can only be selected from the loaded
   * draft list, and leaving that tab clears the selection. */
  selectedRows: SupplierInquiryListItem[]
  onRemoveIds: (ids: string[]) => void
}

/** Row height the virtualizer assumes before measuring (two text lines + py-3). */
const ROW_HEIGHT = 62

/** The selected drafts, each individually removable. Virtualized, so a
 *  select-all over a long draft list stays a screenful of DOM. */
export function InquiryBulkSelectionList({
  selectedRows,
  onRemoveIds,
}: InquiryBulkSelectionListProps) {
  const { translate } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: selectedRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  })

  return (
    <div ref={scrollRef} className="max-h-[70vh] overflow-y-auto">
      <div
        role="list"
        style={{ height: virtualizer.getTotalSize(), position: "relative" }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = selectedRows[virtualRow.index]
          const display = INQUIRY_STATUS_DISPLAY[row.status]
          const meta = [row.reference, formatDate(row.createdAt ?? row.sentAt)]
            .filter(Boolean)
            .join(" · ")
          return (
            <div
              key={row.id}
              role="listitem"
              // Windowed: the rows in the DOM are not the whole selection.
              aria-setsize={selectedRows.length}
              aria-posinset={virtualRow.index + 1}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="pb-2"
            >
              <div className="flex items-center justify-between gap-2 rounded-lg border border-grey4 bg-grey2 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-white">
                      {row.supplierName ?? "—"}
                    </span>
                    {display && (
                      <McPill tone={display.tone}>
                        {translate(display.labelKey)}
                      </McPill>
                    )}
                  </div>
                  {meta && (
                    <p className="mt-0.5 truncate text-xs text-grey5">{meta}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveIds([row.id])}
                  className="shrink-0 text-grey5 hover:text-white"
                  aria-label={translate("MC_INQ_SEND_REMOVE", {
                    name: row.supplierName ?? row.id,
                  })}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
